import { types, flow, castToSnapshot, getSnapshot } from 'mobx-state-tree';
import { navigate, errorResponse } from 'js/services/Network';

import { makeRequest, url } from 'js/services/Network';
import analytics from 'js/services/Analytics';
import { castEventToValue } from 'js/services/FormTools';
import AnonymousId from 'js/services/AnonymousId';

import ShippingSelectorState from 'js/stores/ShippingSelectorState';
import SteppedPage from 'js/stores/SteppedPage';

import ShippingDetail from 'js/stores/types/ShippingDetail';
import ItemSaleInfo from 'js/stores/types/ItemSaleInfo';
import Address from 'js/stores/types/Address';
import CreditCard from 'js/stores/types/CreditCard';
import BankAccount from '../types/BankAccount';

const CheckoutPageState = types
  .model('CheckoutPageState', {
    alias: types.string,
    size: types.maybe(types.string),
    error: types.maybeNull(types.string),
    wasSubmitted: false,
    modalIsVisible: false,
    modalDisplay: '',
    userWantsShippingOptions: false,
    confirmAddress: false,
    shipping_service: types.maybeNull(ShippingDetail),
    shippingSelector: types.optional(ShippingSelectorState, {}),
    full_name: '',
    phone_number: '',
    fee: 0,
    item: types.optional(ItemSaleInfo, {}),
    address: types.optional(Address, {}),
    credit_card: types.optional(CreditCard, {}),
    bank_account: types.optional(BankAccount, {}),
    bonus_applied: 0,
    bonus_available: 0,
    applyingBonus: false,
    sold_out: false,
    discount_code: '',
    discount_data_message: '',
    discount_data_value: 0,
    discount_data_minimum_checkout_value: 0,
    discount_max_usage: 0,
    discount_usages: 0,
    addressInput: false,
    first_transaction: false,
    transaction_count: 0,
    happy_hour_status: 0,
    happy_hour_start_time: 'August, 01, 2024, 17:59:00',
    has_valid_address: false,
  })
  .views(self => ({
    get hasShippingService() {
      return !!self.shipping_service;
    },
    get hasAddressError() {
      return !self.address.hasAddress && self.wasSubmitted;
    },
    get showShippingSelection() {
      return self.address.hasAddress && self.userWantsShippingOptions;
    },
  }))
  .actions(self => ({
    getStepRoute(step) {
      return url('checkout-page', {
        alias: self.alias,
        step,
      });
    },

    initStep(step) {
      self.modalIsVisible = false;
      return self;
    },

    load: flow(function* load() {
      const response = yield makeRequest('checkout-data', self);
      self.item = response.data.item;
      self.fee = response.data.fee;
      self.size = response.data.size;
      self.address = response.data.address;
      self.credit_card = response.data.credit_card;
      self.bank_account = response.data.bank_account;
      self.phone_number = response.data.phone_number;
      self.full_name = response.data.full_name;
      self.bonus_available = response.data.bonus_available;
      self.bonus_applied = response.data.bonus_applied;
      self.sold_out = response.data.sold_out;
      self.first_transaction = response.data.first_transaction;
      self.happy_hour_status = response.data.happy_hour_status;
      self.happy_hour_start_time = response.data.happy_hour_start_time;
      self.transaction_count = response.data.transaction_count;

      self.shippingSelector.initialize({
        alias: self.alias,
        size: self.size,
        destination: self.address,
        onChange: self.setShippingService,
      });

      return response;
    }),

    submit: flow(function* submit() {
      let response;
      if (!self.isSubmitting) {
        self.isSubmitting = true;
        self.error = null;

        try {
          response = yield makeRequest('checkout-item', self, {
            anonymous_id: AnonymousId,
            name: self.full_name,
            phone_number: self.phone_number,
            shipping_service: self.shipping_service.service,
            credit_card: getSnapshot(self.credit_card),
            bank_account: getSnapshot(self.bank_account),
            address: getSnapshot(self.address),
            discount_code: self.discount_code,
            bonus_available: self.bonus_available,
            happy_hour_status: self.happy_hour_status
          });

          analytics.track('Offer Pending', {
            'Order id': response.data.uid,
            'Order Alias': response.data.alias,
            'Item id': self.alias,
            'Item Price': self.item.price,
            'Item Name': self.item.title,
            'Item Size': self.size,
            'Shipping Option': self.shipping_service,
            'Payment Link': self.item.checkout_url,
            'Event Initiated by': 'buyer'
          });

          navigate(url('status-page', response.data));
        } catch (error) {
          self.error = errorResponse(error);
        }

        self.isSubmitting = false;
      }
      return response;
    }),

    showShippingOptions() {
      self.userWantsShippingOptions = true;
      return self;
    },
    editDeliveryAddress() {
      self.addressInput = true;
      self.modalDisplay = 'delivery';
      self.modalIsVisible = true;
    },
    editShipping() {
      self.modalDisplay = 'shipping';
      self.shipping_service === null && self.shippingSelector.load();
      self.modalIsVisible = true;
    },
    editPaymentMethod() {
      self.modalDisplay = 'payment';
      self.modalIsVisible = true;
    },
    applyBonus: flow(function* applyBonus(e) {
      e.preventDefault();
      self.applyingBonus = true;
      const response = yield makeRequest('apply-bonus', self);
      self.applyingBonus = false;
      if(response.data === true){
        self.bonus_applied = self.bonus_available;
        self.bonus_available = 0;

        analytics.track('Reward Credit Applied', {
          'Source Address': '',
          'Destination Address': self.address,
          'Shipping Option': self.shipping_service,
          'Shipping Cost': self.fee,
          'Item Price': self.item.price,
          'item Name': self.item.title,
          'Item Size': self.size,
          'Event Initiated by': 'buyer'
        });

      }
    }),
    openModal() {
      self.modalIsVisible = true;
      return self;
    },
    closeModal() {
      self.modalIsVisible = false;
      return self;
    },

    setShippingService(shippingService) {
      self.shipping_service = shippingService;
      return self;
    },
    setDiscountCode(code) {
      self.discount_code = code;
      return self;
    },
    setDiscountDataMessage(message) {
      self.discount_data_message = message;
      return self;
    },
    setDiscountDataAmount(value) {
      self.discount_data_value = value;
      return self;
    },
    setDiscountDataMinimumCheckoutValue(value) {
      self.discount_data_minimum_checkout_value = value;
      return self;
    },
    setDiscountUsages(value) {
      self.discount_usages = value;
      return self;
    },
    setDiscountMaxUsage(value) {
      self.discount_max_usage = value;
      return self;
    },
    creditCardSaved(creditCard) {
      self.credit_card.setCreditCard(castToSnapshot(creditCard));
      self.modalIsVisible = false;
      return self;
    },
    addressSaved(savedAddress) {
     /* if(!self.credit_card.use_shipping_address){
        self.credit_card.setBillingAddress(castToSnapshot(savedAddress));
      }else{
        if (!savedAddress || self.address.formatted_address !== savedAddress.formatted_address) {
          self.address.setAddress(castToSnapshot(savedAddress));
          self.shipping_service = null;
        }
        self.modalIsVisible = false;
      }*/

      if (!savedAddress || self.address.formatted_address !== savedAddress.formatted_address) {
        self.address.setAddress(castToSnapshot(savedAddress));
        self.shipping_service = null;
      }
      self.modalIsVisible = false;
      return self;
    },
    setFullName(full_name) {
      self.full_name = castEventToValue(full_name);
      return self;
    },
    setPhoneNumber(phone_number) {
      self.phone_number = castEventToValue(phone_number);
      return self;
    },
    setAddressInput(value) {
      self.addressInput = value;
      return self;
    },
    setBonusAvailable(value) {
      self.bonus_available = value;
      return self;
    },
  }));

export default types.compose(
  SteppedPage,
  CheckoutPageState,
);
